import {
  bandOf,
  conditionDone,
  INDUSTRIES,
  MISSIONS,
  planTarget,
  SCORE_BANDS,
  type ScoreTrace,
} from '@cyberscore/contracts';
import { Flag, Undo2 } from 'lucide-react';
import { useMemo, useState } from 'react';
import { hashQuery, href, navigate, useHash } from '../../lib/router';
import type { AssessmentSummary } from '../assessments/api';
import { useDecisions } from '../decisions/api';
import { bandLabel } from '../portfolio/format';
import { CardHead, MissionIcon, controlLink } from './parts';

const MIN = 300;
const MAX = 1000;

/** The target in the address (`#/plan?target=650&skip=IA-01,EP-02`), so a plan prints and shares as set. */
function useTarget() {
  const q = hashQuery(useHash());
  const n = Number(q.get('target'));
  const target = Number.isInteger(n) && n >= MIN && n <= MAX ? n : null;
  const skip = (q.get('skip') ?? '').split(',').filter((s) => /^[A-Z]{2}-\d{2}$/.test(s));
  const go = (t: number | null, s: string[]) => {
    const p = new URLSearchParams();
    if (t !== null) p.set('target', String(t));
    if (t !== null && s.length) p.set('skip', s.join(','));
    const qs = p.toString().replace(/%2C/g, ',');
    navigate(`${href.plan()}${qs ? `?${qs}` : ''}`, { replace: true });
  };
  return { target, skip, go };
}

/**
 * The target score planner (task 77): the business picks a band or a score, and sees the fewest steps that
 * get there, counted together. A step it can't take on can be set aside and the plan finds another way;
 * the insurer's conditions are always in it. It arrives already aimed at the next band up (task 107,
 * decision 0074), so there is a plan before anyone picks anything; picking another is one click.
 */
export function TargetCard({ a, trace, score }: { a: AssessmentSummary; trace: ScoreTrace; score: number }) {
  const { target: picked, skip, go } = useTarget();
  const decisions = useDecisions(a.id, a.status === 'decided');
  const conditions = useMemo(() => (decisions.data?.conditions ?? []).filter((c) => c.control && !conditionDone(c.state)), [decisions.data]); // prettier-ignore
  const must = useMemo(() => conditions.map((c) => c.control!), [conditions]);
  const [typed, setTyped] = useState('');
  const bands = SCORE_BANDS.filter((b) => b.min > score && b.key !== 'POOR');
  const recommended = bands[0] ?? null;
  const target = picked ?? recommended?.min ?? null;
  const plan = useMemo(() => (target === null ? null : planTarget(trace, target, { must, skip })), [trace, target, must, skip]); // prettier-ignore
  const named = (control: string) => trace.controls.find((c) => c.control === control)?.name ?? control;
  const typedOk = /^\d{3,4}$/.test(typed) && Number(typed) > score && Number(typed) <= MAX;

  return (
    <section className="bcard mt-l target-card" aria-labelledby="target-title">
      <CardHead tone="acc" icon={<Flag size={18} />} title={<span id="target-title">Aim for a score</span>} sub="Pick where you want to be. You see the fewest steps that get there, with their points counted together." />
      <div className="target-pick">
        {bands.length > 0 && (
          <div className="bseg" role="group" aria-label="Aim for a band">
            {bands.map((b) => <button key={b.key} type="button" aria-selected={target === b.min} onClick={() => go(b.min, skip)}>{bandLabel(b.key)} · {b.min}</button>)}
          </div>
        )}
        <form className="target-own" onSubmit={(e) => { e.preventDefault(); if (typedOk) { go(Number(typed), skip); setTyped(''); } }}>
          <label htmlFor="target-score" className="small muted">Or a score</label>
          <input id="target-score" className="input" inputMode="numeric" placeholder={String(Math.min(MAX, score + 50))} value={typed} onChange={(e) => setTyped(e.target.value.replace(/\D/g, '').slice(0, 4))} />
          <button type="submit" className="bbtn sm" disabled={!typedOk}>Plan it</button>
        </form>
        {picked !== null && picked !== recommended?.min && recommended && <button type="button" className="bbtn sm soft" onClick={() => go(null, [])}>Back to our pick</button>}
      </div>
      {recommended && target === recommended.min && <p className="muted small mt-s">We picked {bandLabel(recommended.key).toLowerCase()}, the next band up: the most an insurer notices for the effort. Pick another if you like.</p>}
      {target !== null && target <= score && <p className="muted mt-s">You are at {score} already. Pick a higher score.</p>}
      {plan && target !== null && target > score && (
        <>
          <p className="target-sum" role="status">
            {plan.reachable ? (
              <><b>{plan.steps.length} step{plan.steps.length === 1 ? '' : 's'}</b> across {plan.missions.length} mission{plan.missions.length === 1 ? '' : 's'} take you from {score} to <b>{plan.reaches}</b> ({bandLabel(bandOf(plan.reaches)).toLowerCase()}).</>
            ) : (
              <>Your answers can reach <b>{plan.max}</b> at most{plan.skipped.length ? ' with the steps set aside left out' : ''}, short of {target}. {plan.skipped.length ? 'Put some back, or aim lower.' : 'These steps get you closest.'}</>
            )}
          </p>
          <div className="target-groups">
            {plan.missions.map((g) => {
              const m = MISSIONS.find((x) => x.id === g.id);
              const mine = plan.steps.filter((s) => s.mission === g.id);
              return (
                <section key={g.id} className="target-group" aria-label={g.name}>
                  <h4 className="row gap-s"><MissionIcon id={g.id} size={15} />{g.name}{m?.pack ? ` · ${INDUSTRIES[m.pack]}` : ''}<span className="muted tiny">{g.steps} step{g.steps === 1 ? '' : 's'}</span></h4>
                  <ul className="target-steps">
                    {mine.map((s) => (
                      <li key={s.control} className="task">
                        <a className="grow" href={controlLink(a, trace, s.control, s.mission).to}>
                          <span className="tname">{s.name}</span>
                          {s.lifts_cap && <span className="bad-t tiny">Lifts the cap</span>}
                        </a>
                        {s.condition ? <span className="tag pri">Your insurer’s condition</span> : <button type="button" className="bbtn xs soft" onClick={() => go(target, [...skip, s.control])} aria-label={`Set aside: ${s.name}`}>Can’t do this</button>}
                        <span className="num ok-t strong">+{s.points}</span>
                      </li>
                    ))}
                  </ul>
                </section>
              );
            })}
          </div>
          {plan.skipped.length > 0 && (
            <div className="target-skipped small">
              <span className="muted">Set aside:</span>
              {plan.skipped.map((c) => <button key={c} type="button" className="bbtn xs soft" onClick={() => go(target, skip.filter((x) => x !== c))} aria-label={`Put back: ${named(c)}`}><Undo2 size={12} /> {named(c)}</button>)}
            </div>
          )}
          <p className="muted tiny mt-s">A step’s points are its own, from today. Together they add {plan.reaches - score}, because the score isn’t a simple sum.</p>
        </>
      )}
    </section>
  ); // prettier-ignore
}
